/**
 * Graph Loader
 *
 * Handles loading and saving the blessed dependency graph file.
 * The graph is stored at architecture/dependencies.json in the workspace root.
 *
 * File format (schema aimed at AI consumers):
 * {
 *     "aiInstructions": "...how AI should use the per-project fields...",
 *     "projects": {
 *         "<project>": { level, framework, shortDescription,
 *                        responsibilitiesFile, designFile, dependsOn }
 *     }
 * }
 *
 * `framework` is the project's libType — the SET of runtime environments it is
 * validated to run in, drawn from browser | react | angular | node | express
 * (e.g. ["browser","node"]). It comes from the project's `framework:` nx tags
 * and is enforced across edges by the `library-types-match-client` rule.
 *
 * The legacy format (flat { "<project>": { level, dependsOn } } map) is still
 * readable so validation against a pre-upgrade file produces a clean
 * "re-run architecture:generate" diff instead of a parse failure.
 */

import * as fs from 'fs';
import * as path from 'path';
import type { EnhancedGraph, GraphEntry } from './graph-sorter';
import type { ApiContracts, ExternalSystemDecls, ProjectApiRelations } from './api-usage/api-relations';
import { toError } from '../toError';

/**
 * Default path for the dependencies file (relative to workspace root)
 */
export const DEFAULT_GRAPH_PATH = 'architecture/dependencies.json';

/**
 * Top-level instructions embedded in dependencies.json telling AI how to use
 * the per-project metadata fields.
 */
export const AI_INSTRUCTIONS =
    "Each project's shortDescription is only a summary. BEFORE adding code to a project, " +
    'read its responsibilitiesFile for the full responsibilities (what belongs in that ' +
    'project and what does not), and read its designFile to understand the DI design ' +
    'before reading the code. Use the entries in `commands` to regenerate these files ' +
    'or display any of the graphs in a browser. To hide a project (its box AND every ' +
    "edge touching it) from BOTH rendered architecture graphs, add the nx tag " +
    "'drawOnGraph:false' to that project's project.json tags and regenerate; it stays " +
    'in this file (marked "drawOnGraph": false) but is omitted from the HTML. A role:server ' +
    'with no webpiecesRuntime package anywhere in its dependency closure and no apiRelations is ' +
    'omitted from the RUNTIME drawing the same way — it speaks none of the webpieces runtime, so ' +
    'it can only ever draw as a disconnected box; it stays in both JSON files.';

/**
 * Named command → "command — what it does" map embedded in dependencies.json.
 */
export type CommandMap = Record<string, string>;

/**
 * Commands embedded in dependencies.json so AI (and humans) know how to
 * regenerate and DISPLAY the architecture + design graphs. These work in any
 * repo consuming @webpieces/nx-webpieces-rules.
 */
export const GRAPH_COMMANDS: CommandMap = {
    regenerateArchitecture:
        'pnpm nx run architecture:generate — rewrites architecture/dependencies.json and ' +
        'architecture/runtime-dependencies.json; run after adding/removing project dependencies',
    visualizeArchitecture:
        'pnpm nx run architecture:visualize — opens the monorepo dependency graph (this file) ' +
        'as HTML in a browser',
    visualizeRuntimeArchitecture:
        'pnpm nx run architecture:visualize-runtime — opens the runtime microservice call graph ' +
        'as HTML in a browser',
    regenerateDesigns:
        "pnpm nx run-many --target=di-graph-generate — rewrites every project's " +
        'design.json/design.md (also runs automatically on every build)',
    visualizeDesign:
        "pnpm wp-design-visualize <project> — opens a project's DI designs (its designFile) as " +
        'HTML, one graph per controller with the controller at the top; no args = interactive picker',
};

/**
 * The full contents of architecture/dependencies.json.
 */
export class DependenciesFile {
    constructor(
        public readonly aiInstructions: string,
        public readonly commands: CommandMap,
        public readonly projects: EnhancedGraph,
        /**
         * Every API contract's per-method trigger table (kind + queue name + path).
         *
         * MUST be persisted, for the same reason `callsService` must: the runtime graph is derived
         * SOLELY from this file, and `validate-runtime-architecture` re-derives from the LOADED copy
         * while generate derives from the in-memory one. A field that is scanned but not written
         * makes those two inputs differ, and the validator reports a diff no one can fix.
         */
        public readonly apiContracts: ApiContracts = {},
        /**
         * Declared external systems (databases, buckets, ...) keyed by identity. Persisted for the
         * same reason apiContracts is: the runtime graph is derived SOLELY from this file, so a
         * declaration that is scanned but not written would make generate and validate disagree.
         */
        public readonly externalSystems: ExternalSystemDecls = {}
    ) {}
}

/**
 * Load the blessed graph from disk. Understands both the current wrapper
 * format and the legacy flat map (which loads with empty aiInstructions).
 *
 * @param workspaceRoot - Absolute path to workspace root
 * @param graphPath - Relative path to graph file (default: architecture/dependencies.json)
 * @returns The blessed graph file, or null if it doesn't exist
 */
export function loadBlessedGraph(
    workspaceRoot: string,
    graphPath: string = DEFAULT_GRAPH_PATH
): DependenciesFile | null {
    const fullPath = path.join(workspaceRoot, graphPath);

    if (!fs.existsSync(fullPath)) {
        return null;
    }

    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions
    try {
        const content = fs.readFileSync(fullPath, 'utf-8');
        const parsed = JSON.parse(content);
        if (parsed !== null && typeof parsed === 'object' && 'projects' in parsed) {
            return new DependenciesFile(
                typeof parsed.aiInstructions === 'string' ? parsed.aiInstructions : '',
                parsed.commands !== null && typeof parsed.commands === 'object' ? (parsed.commands as CommandMap) : {},
                parsed.projects as EnhancedGraph,
                // Absent in a file written before apiContracts existed; an empty table degrades the
                // runtime graph to unnamed per-pair queues rather than failing to load.
                parsed.apiContracts !== null && typeof parsed.apiContracts === 'object'
                    ? (parsed.apiContracts as ApiContracts)
                    : {},
                // Absent in any file written before external systems could be declared; an empty
                // table simply draws no shaped nodes, which is exactly the old rendering.
                parsed.externalSystems !== null && typeof parsed.externalSystems === 'object'
                    ? (parsed.externalSystems as ExternalSystemDecls)
                    : {}
            );
        }
        // Legacy flat format: the whole object is the project map
        return new DependenciesFile('', {}, parsed as EnhancedGraph);
    } catch (err: unknown) {
        const error = toError(err);
        throw new Error(`Failed to load graph from ${fullPath}`, { cause: error });
    }
}

/**
 * Format the dependencies file as JSON with multi-line arrays for readability
 */
function formatGraphJson(file: DependenciesFile): string {
    const lines: string[] = ['{'];
    lines.push(`    "aiInstructions": ${JSON.stringify(file.aiInstructions)},`);
    lines.push(`    "commands": {`);
    const commandNames = Object.keys(file.commands);
    commandNames.forEach((name: string, index: number) => {
        const comma = index === commandNames.length - 1 ? '' : ',';
        lines.push(`        ${JSON.stringify(name)}: ${JSON.stringify(file.commands[name])}${comma}`);
    });
    lines.push(`    },`);
    lines.push(...apiContractsLines(file.apiContracts));
    lines.push(...externalSystemsLines(file.externalSystems));
    lines.push(`    "projects": {`);

    const keys = Object.keys(file.projects).sort();
    keys.forEach((key: string, index: number) => {
        const entry = file.projects[key];
        const isLast = index === keys.length - 1;
        const comma = isLast ? '' : ',';

        lines.push(`        ${JSON.stringify(key)}: {`);
        lines.push(...formatEntryLines(entry));
        lines.push(`        }${comma}`);
    });

    lines.push('    }');
    lines.push('}');
    return lines.join('\n') + '\n';
}

/**
 * The `"apiContracts": {...}` block (4-space indent), with a trailing comma since `projects` always
 * follows it. Omitted entirely when empty, so a repo with no api contracts keeps the old file shape.
 * The scanner already sorts contracts by name and leaves methods in declaration order, so pretty
 * JSON.stringify is deterministic.
 */
// webpieces-disable no-function-outside-class -- module-scope formatter, matches the sibling formatters here
function apiContractsLines(contracts: ApiContracts): string[] {
    if (Object.keys(contracts).length === 0) return [];
    const pretty = JSON.stringify(contracts, null, 4).split('\n');
    return pretty.map((line: string, index: number) => {
        const prefix = index === 0 ? '"apiContracts": ' : '';
        const suffix = index === pretty.length - 1 ? ',' : '';
        return `    ${prefix}${line}${suffix}`;
    });
}

/**
 * The `"externalSystems": {...}` block (4-space indent), with a trailing comma since `projects`
 * always follows. Omitted entirely when empty, so a repo that declares none keeps the exact file
 * shape it had before this existed — which is what makes adopting it a no-op diff.
 */
// webpieces-disable no-function-outside-class -- module-scope formatter, matches the sibling formatters here
function externalSystemsLines(systems: ExternalSystemDecls): string[] {
    if (Object.keys(systems).length === 0) return [];
    const sorted: ExternalSystemDecls = {};
    for (const identity of Object.keys(systems).sort()) sorted[identity] = systems[identity];
    const pretty = JSON.stringify(sorted, null, 4).split('\n');
    return pretty.map((line: string, index: number) => {
        const prefix = index === 0 ? '"externalSystems": ' : '';
        const suffix = index === pretty.length - 1 ? ',' : '';
        return `    ${prefix}${line}${suffix}`;
    });
}

/**
 * Format one project entry's fields (12-space indent). Optional metadata
 * fields are only emitted when present.
 */
function formatEntryLines(entry: GraphEntry): string[] {
    const lines: string[] = [];
    lines.push(`            "level": ${entry.level},`);

    pushOptionalArrayField(lines, 'framework', entry.framework);
    pushOptionalField(lines, 'role', entry.role);
    // Right after role — role says what the project IS, this says what runtime it speaks. MUST be
    // persisted: validate re-derives the runtime graph from this file, so a field written only
    // in-memory by generate would make the two derive different graphs.
    pushOptionalArrayField(lines, 'webpiecesRuntime', entry.webpiecesRuntime);
    pushOptionalField(lines, 'serviceName', entry.serviceName);
    pushOptionalCallsServiceField(lines, entry.callsService);
    pushOptionalBooleanField(lines, 'drawOnGraph', entry.drawOnGraph);
    pushOptionalField(lines, 'shortDescription', entry.shortDescription);
    pushOptionalField(lines, 'responsibilitiesFile', entry.responsibilitiesFile);
    pushOptionalField(lines, 'designFile', entry.designFile);
    pushApiRelationsField(lines, entry.apiRelations);

    if (entry.dependsOn.length === 0) {
        lines.push(`            "dependsOn": []`);
    } else {
        lines.push(`            "dependsOn": [`);
        entry.dependsOn.forEach((dep: string, depIndex: number) => {
            const depComma = depIndex === entry.dependsOn.length - 1 ? '' : ',';
            lines.push(`                ${JSON.stringify(dep)}${depComma}`);
        });
        lines.push(`            ]`);
    }
    return lines;
}

/**
 * Emit one optional string field (12-space indent), skipped when undefined.
 */
function pushOptionalField(lines: string[], field: string, value: string | undefined): void {
    if (value !== undefined) {
        lines.push(`            ${JSON.stringify(field)}: ${JSON.stringify(value)},`);
    }
}

/**
 * Emit the optional `callsService` field (12-space indent). Unlike serviceName it may be EITHER a
 * single service name (string) OR an { apiClassName: serviceName } map, so it is emitted with
 * JSON.stringify to cover both inline. MUST be persisted here: the runtime graph's target resolution
 * reads it back from the committed dependencies.json during validate — dropping it would make
 * generate (which enriches it in-memory) and validate (which loads this file) derive different graphs.
 */
// webpieces-disable no-function-outside-class -- module-scope formatter, matches the sibling push*Field helpers here
function pushOptionalCallsServiceField(
    lines: string[],
    value: string | Record<string, string> | undefined,
): void {
    if (value !== undefined) {
        lines.push(`            "callsService": ${JSON.stringify(value)},`);
    }
}

/**
 * Emit one optional boolean field (12-space indent) as a raw JSON boolean
 * (e.g. `"drawOnGraph": false,`), skipped when undefined.
 */
// webpieces-disable no-function-outside-class -- module-scope formatter, matches the sibling push*Field helpers here
function pushOptionalBooleanField(lines: string[], field: string, value: boolean | undefined): void {
    if (value !== undefined) {
        lines.push(`            ${JSON.stringify(field)}: ${JSON.stringify(value)},`);
    }
}

/**
 * Emit one optional string-array field (12-space indent) as a compact inline
 * JSON array (e.g. `"framework": ["browser","node"],`), skipped when undefined.
 */
function pushOptionalArrayField(lines: string[], field: string, value: string[] | undefined): void {
    if (value !== undefined) {
        lines.push(`            ${JSON.stringify(field)}: ${JSON.stringify(value)},`);
    }
}

/**
 * Emit the optional `apiRelations` object (pretty, multi-line, reindented under
 * the 12-space entry block) with a trailing comma, since `dependsOn` always
 * follows it. Skipped when absent/empty so plain-lib-only projects stay compact.
 * The scanner already sorts owners + refs, so the JSON is deterministic.
 */
// webpieces-disable no-function-outside-class -- module-scope formatter, matches the sibling push*Field helpers here
function pushApiRelationsField(lines: string[], value: ProjectApiRelations | undefined): void {
    if (value === undefined || Object.keys(value).length === 0) return;
    const pretty = JSON.stringify(value, null, 4).split('\n');
    pretty.forEach((line: string, index: number) => {
        const prefix = index === 0 ? '"apiRelations": ' : '';
        const suffix = index === pretty.length - 1 ? ',' : '';
        lines.push(`            ${prefix}${line}${suffix}`);
    });
}

/**
 * Save the graph to disk in the wrapper format with the standard aiInstructions.
 *
 * @param graph - The enriched project graph to save
 * @param workspaceRoot - Absolute path to workspace root
 * @param graphPath - Relative path to graph file (default: architecture/dependencies.json)
 */
export function saveGraph(
    graph: EnhancedGraph,
    workspaceRoot: string,
    graphPath: string = DEFAULT_GRAPH_PATH,
    apiContracts: ApiContracts = {},
    externalSystems: ExternalSystemDecls = {}
): void {
    const fullPath = path.join(workspaceRoot, graphPath);
    const dir = path.dirname(fullPath);

    // Ensure directory exists
    if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
    }

    // Sort keys for deterministic output
    const sortedGraph: EnhancedGraph = {};
    const sortedKeys = Object.keys(graph).sort();
    for (const key of sortedKeys) {
        sortedGraph[key] = graph[key];
    }

    const content = formatGraphJson(
        new DependenciesFile(AI_INSTRUCTIONS, GRAPH_COMMANDS, sortedGraph, apiContracts, externalSystems)
    );
    fs.writeFileSync(fullPath, content, 'utf-8');
}

/**
 * Check if the graph file exists
 */
export function graphFileExists(
    workspaceRoot: string,
    graphPath: string = DEFAULT_GRAPH_PATH
): boolean {
    const fullPath = path.join(workspaceRoot, graphPath);
    return fs.existsSync(fullPath);
}
